import { inject, Injectable } from '@angular/core';
import { Coupon, OwnerKind, VehicleSelection } from '@core/models/swagger';
import { Recommendation } from '@core/models/swagger/recommendation';
import {
  CoveragesSectionSummaryLite,
  CoverageSummaryLite,
  PriceDetailSummaryLite,
  SectionIdSummaryLite,
  SectionSummaryLite,
  SubSectionIdSummaryLite,
  SubSectionSummaryLite,
  SummaryLiteData,
  VehicleInformationSummaryLite,
} from '@core/models/swagger/summaryLiteData';
import {
  CAR_TYPES,
  FUEL_TYPES,
  OFFERS,
  VEHICLE_CATEGORIES_TRANSLATE,
  VEHICLE_FUEL_TYPES_TRANSLATE,
} from '@global/constants';
import { extractPercentage, findParameterResultById, getMayorSfClass, getPersonType, hasContent } from '@global/utils';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { getCoupon } from '@store/flow-data/coupon';
import {
  getBrand,
  getCarType,
  getFuel,
  getHorsePower,
  getModel,
  getPossibleVehicles,
  getSelectedVehicle,
  getVehicleDoors,
} from '@store/flow-data/general';
import { combineLatest, first, map, Observable } from 'rxjs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { Patterns } from '@global/enums';
import { QbItaParameterResult } from '@fwmovilidad/qb-home-ita-components-lib';

dayjs.extend(customParseFormat);

@Injectable({
  providedIn: 'root',
})
export class SummaryHelperService {
  private readonly store: Store;
  private readonly translateService: TranslateService;

  constructor() {
    this.store = inject(Store);
    this.translateService = inject(TranslateService);
  }

  getSummaryVehicleInformationString(vehicleSelectionType: VehicleSelection, isReduced: boolean): Observable<string> {
    const categoriesTranslateMap = new Map(
      VEHICLE_CATEGORIES_TRANSLATE.map(categoriesTranslate => [categoriesTranslate.id, categoriesTranslate.value])
    );
    const fuelTypesTranslateMap = new Map(
      VEHICLE_FUEL_TYPES_TRANSLATE.map(fuelTypesTranslate => [fuelTypesTranslate.id, fuelTypesTranslate.value])
    );

    const summaryData$ =
      vehicleSelectionType === VehicleSelection.BY_BRAND_MODEL
        ? this.getSummaryVehicleInformationByBrand()
        : this.getSummaryVehicleInformationByCodeOrPhoto();

    return summaryData$.pipe(
      map(summaryData => {
        const result = isReduced
          ? [summaryData?.manufacturer, summaryData?.model]
          : [
              summaryData?.model,
              fuelTypesTranslateMap.get(summaryData?.engineType) ?? summaryData?.engineType,
              categoriesTranslateMap.get(summaryData?.category) ?? summaryData?.category,
              summaryData?.PsKw,
              summaryData?.doors,
            ];

        return result.filter(value => !!value).join(' | ');
      })
    );
  }

  getSummaryVehicleInformation(vehicleSelectionType: VehicleSelection): Observable<VehicleInformationSummaryLite> {
    return vehicleSelectionType === VehicleSelection.BY_BRAND_MODEL
      ? this.getSummaryVehicleInformationByBrand()
      : this.getSummaryVehicleInformationByCodeOrPhoto();
  }

  private getSummaryVehicleInformationByBrand(): Observable<VehicleInformationSummaryLite> {
    return combineLatest([
      this.store.select(getBrand),
      this.store.select(getModel),
      this.store.select(getFuel),
      this.store.select(getCarType),
      this.store.select(getHorsePower),
      this.store.select(getVehicleDoors),
      this.store.select(getCoupon),
    ]).pipe(
      first(),
      map(([brand, model, fuel, carType, horsePower, doors, coupon]) => {
        return {
          manufacturer: brand.selectedOption.parameterId,
          manufacturingYear: coupon?.carData?.manufacturingYear,
          model: model.selectedOption.parameterName,
          engineType: fuel.selectedOption.parameterName,
          category: carType.selectedOption.parameterName,
          PsKw: horsePower.selectedOption.parameterName,
          doors: `Türen: ${doors}`,
        };
      })
    );
  }

  private getSummaryVehicleInformationByCodeOrPhoto(): Observable<VehicleInformationSummaryLite> {
    return combineLatest([
      this.store.select(getPossibleVehicles),
      this.store.select(getSelectedVehicle),
      this.store.select(getCoupon),
    ]).pipe(
      first(),
      map(([possibleVehicles, selectedVehicleIndex, coupon]) => {
        const selectedVehicle = possibleVehicles[selectedVehicleIndex];

        return {
          manufacturer: selectedVehicle?.manufacturer,
          manufacturingYear: coupon?.carData?.manufacturingYear,
          model: selectedVehicle?.modelDescription,
          engineType: findParameterResultById(FUEL_TYPES, selectedVehicle?.engineType)?.parameterName,
          category: findParameterResultById(CAR_TYPES, selectedVehicle?.category)?.parameterName,
          PsKw: `${selectedVehicle?.power?.ps} PS / ${selectedVehicle?.power?.kw} KW`,
        };
      })
    );
  }

  buildSummaryLiteData(
    data,
    coupon: Coupon,
    showCoverages: boolean,
    sectionExpandedId: SectionIdSummaryLite,
    subSectionExpandedId: SubSectionIdSummaryLite
  ): SummaryLiteData {
    const subtitle = data?.vehicleSummaryInfo;

    const priceDetail: PriceDetailSummaryLite = {
      price: data?.recommendations?.price?.default,
      frequency: data?.recommendations?.price?.frequency,
      currency: data?.recommendations?.currency,
    };

    const deducible = data?.deductibleOption?.parameterName;

    const carInsurers: QbItaParameterResult[] = data?.carInsurers;

    const summarySections = [
      this.buildPolicyholderSection(coupon, sectionExpandedId, subSectionExpandedId),
      this.buildVehicleAndPreviousInsuranceSection(coupon, sectionExpandedId, subSectionExpandedId, carInsurers),
      this.buildPaymentSection(coupon, sectionExpandedId, subSectionExpandedId),
    ].filter(section => section != undefined);

    const coveragesSections = showCoverages ? this.buildCoveragesSections(data?.recommendations) : undefined;

    return {
      title: findParameterResultById(OFFERS, coupon?.insuranceData?.productLine)?.parameterName,
      subtitle,
      priceDetail,
      deducible,
      coveragesSections,
      summarySections,
    };
    // return Mocks.getSummaryLiteData();
  }

  private buildCoveragesSections(recommendations: Recommendation): CoveragesSectionSummaryLite[] {
    let coveragesSections: CoveragesSectionSummaryLite[] = [];

    const coveragesSection: CoveragesSectionSummaryLite = {
      title: this.translateService.instant('SUMMARY_LITE.SECTION.COVERAGE.NORMAL_COVERAGE.TITLE'),
      coverages: this.getSelectedCoverages(recommendations),
    };

    if (coveragesSection.coverages?.length) {
      coveragesSections.push(coveragesSection);
    }

    return coveragesSections;
  }

  private buildPolicyholderSection(
    coupon: Coupon,
    sectionExpandedId: SectionIdSummaryLite,
    subSectionExpandedId: SubSectionIdSummaryLite
  ): SectionSummaryLite {
    const id = SectionIdSummaryLite.POLICYHOLDER;
    const subSections = this.buildPolicyholderSubSections(coupon, subSectionExpandedId);

    if (!subSections?.length && sectionExpandedId !== id) {
      return;
    }

    return {
      id,
      title: this.translateService.instant('SUMMARY_LITE.SECTION.POLICYHOLDER.TITLE'),
      isExpanded: id === sectionExpandedId ? true : false,
      icon: 'verti-user-16',
      subSections,
    };
  }

  private buildVehicleAndPreviousInsuranceSection(
    coupon: Coupon,
    sectionExpandedId: SectionIdSummaryLite,
    subSectionExpandedId: SubSectionIdSummaryLite,
    carInsurers: QbItaParameterResult[]
  ): SectionSummaryLite {
    const id = SectionIdSummaryLite.VEHICLE_AND_PREV_INSURANCE;
    const subSections = this.buildVehicleAndPreviousInsuranceSubSections(coupon, carInsurers, subSectionExpandedId);

    if (!subSections?.length && sectionExpandedId !== id) {
      return;
    }

    return {
      id,
      title: this.translateService.instant('SUMMARY_LITE.SECTION.VEHICLE_AND_PREV_INSURANCE.TITLE'),
      isExpanded: id === sectionExpandedId ? true : false,
      icon: 'verti-car-de-16',
      subSections,
    };
  }

  private buildPaymentSection(
    coupon: Coupon,
    sectionExpandedId: SectionIdSummaryLite,
    subSectionExpandedId: SubSectionIdSummaryLite
  ): SectionSummaryLite {
    const id = SectionIdSummaryLite.PAYMENT;
    const subSections = this.buildPaymentSubSection(coupon, subSectionExpandedId);

    if (!subSections?.length && sectionExpandedId !== id) {
      return;
    }

    return {
      id,
      title: this.translateService.instant('SUMMARY_LITE.SECTION.PAYMENT.TITLE'),
      isExpanded: id === sectionExpandedId ? true : false,
      icon: 'verti-payment-16',
      subSections,
    };
  }

  // Build SubSections
  private buildPolicyholderSubSections(
    coupon: Coupon,
    subSectionExpandedId: SubSectionIdSummaryLite
  ): SubSectionSummaryLite[] | [] {
    let policyholderSubSections: SubSectionSummaryLite[] = [];

    const policyholder = getPersonType(coupon?.policyholder);
    const address = policyholder?.address;
    const contacts = policyholder?.contacts;
    const partner = coupon?.partner;

    const policyholderSubSection: SubSectionSummaryLite = {
      id: SubSectionIdSummaryLite.POLICYHOLDER,
      title: this.translateService.instant('SUMMARY_LITE.SECTION.POLICYHOLDER.SUBSECTION.POLICYHOLDER.TITLE'),
      name:
        policyholder && 'firstname' in policyholder && policyholder.firstname && policyholder.surname
          ? `${policyholder.firstname} ${policyholder.surname}`
          : '-',
      address: address?.street && address?.houseNumber ? `${address.street} ${address.houseNumber}` : '-',
      city: address?.city && address?.postcode ? `${address.postcode} ${address.city}` : '-',
      email: contacts?.email ? contacts.email : '-',
      phone: contacts?.phonePrefix && contacts?.phoneNumber ? `${contacts.phonePrefix} ${contacts.phoneNumber}` : '-',
    };

    if (subSectionExpandedId === SubSectionIdSummaryLite.POLICYHOLDER || hasContent(policyholderSubSection)) {
      policyholderSubSections.push(policyholderSubSection);
    }

    const rawBirthday =
      policyholder && 'birthdayOldestChild' in policyholder && policyholder?.birthdayOldestChild
        ? policyholder.birthdayOldestChild
        : undefined;

    const formattedBirthday = rawBirthday ? dayjs(rawBirthday).format(Patterns.DATE_DOT_DDMMYYYY) : undefined;

    const familySubSection: SubSectionSummaryLite = {
      id: SubSectionIdSummaryLite.FAMILY,
      title: this.translateService.instant('SUMMARY_LITE.SECTION.POLICYHOLDER.SUBSECTION.FAMILY.TITLE'),
      partner: partner?.firstname && partner?.surname ? `${partner.firstname} ${partner.surname}` : '-',
      kinder: formattedBirthday ?? '-',
    };

    if (subSectionExpandedId === SubSectionIdSummaryLite.FAMILY || hasContent(familySubSection)) {
      policyholderSubSections.push(familySubSection);
    }

    return policyholderSubSections;
  }

  private buildVehicleAndPreviousInsuranceSubSections(
    coupon: Coupon,
    carInsurers: QbItaParameterResult[],
    subSectionExpandedId: SubSectionIdSummaryLite
  ): SubSectionSummaryLite[] {
    let vehicleAndPrevInsuranceSubSections: SubSectionSummaryLite[] = [];

    const vehicleOwner = getPersonType(coupon?.vehicleOwner);
    const address = vehicleOwner?.address;
    const carData = coupon?.carData;
    const licensePlate = carData?.licensePlate;
    const previousInsurance = coupon?.previousInsuranceData;
    const insuranceData = coupon?.insuranceData;

    const vehicleOwnerSubSection: SubSectionSummaryLite = {
      id: SubSectionIdSummaryLite.VEHICLE_OWNER,
      title: this.translateService.instant(
        'SUMMARY_LITE.SECTION.VEHICLE_AND_PREV_INSURANCE.SUBSECTION.VEHICLE_OWNER.TITLE'
      ),
      name:
        vehicleOwner && 'firstname' in vehicleOwner && vehicleOwner?.firstname && vehicleOwner?.surname
          ? `${vehicleOwner.firstname} ${vehicleOwner.surname}`
          : '-',
      address: address?.street && address?.houseNumber ? `${address.street} ${address.houseNumber}` : '-',
      city: address?.city && address?.postcode ? `${address.postcode} ${address.city}` : '-',
    };

    if (subSectionExpandedId === SubSectionIdSummaryLite.VEHICLE_OWNER || hasContent(vehicleOwnerSubSection)) {
      vehicleAndPrevInsuranceSubSections.push(vehicleOwnerSubSection);
    }

    const vehicleInfoSubSection: SubSectionSummaryLite = {
      id: SubSectionIdSummaryLite.VEHICLE_INFO,
      title: this.translateService.instant(
        'SUMMARY_LITE.SECTION.VEHICLE_AND_PREV_INSURANCE.SUBSECTION.VEHICLE_INFO.TITLE'
      ),
      mileage: carData?.mileage ?? '-',
      registration:
        licensePlate?.prefix && licensePlate?.suffix1 && licensePlate?.suffix2
          ? `${licensePlate.prefix} ${licensePlate.suffix1} ${licensePlate.suffix2}`
          : '-',
      id_vehicle: carData?.identificationNumber ?? '-',
    };

    if (subSectionExpandedId === SubSectionIdSummaryLite.VEHICLE_INFO || hasContent(vehicleInfoSubSection)) {
      vehicleAndPrevInsuranceSubSections.push(vehicleInfoSubSection);
    }

    const previousInsuranceSubSection: SubSectionSummaryLite = {
      id: SubSectionIdSummaryLite.PREVIOUS_INSURANCE,
      title: this.translateService.instant(
        'SUMMARY_LITE.SECTION.VEHICLE_AND_PREV_INSURANCE.SUBSECTION.PREVIOUS_INSURANCE.TITLE'
      ),
      sf_class: getMayorSfClass(insuranceData?.ncdClassFullyComprehensive, insuranceData?.ncdClassLiability) ?? '-',
      insurer:
        carInsurers?.find(option => option.parameterId === previousInsurance?.previousInsurer)?.parameterName ?? '-',
      previous_policy_number: previousInsurance?.previousPolicyNumber ?? '-',
    };

    if (
      subSectionExpandedId === SubSectionIdSummaryLite.PREVIOUS_INSURANCE ||
      hasContent(previousInsuranceSubSection)
    ) {
      vehicleAndPrevInsuranceSubSections.push(previousInsuranceSubSection);
    }

    return vehicleAndPrevInsuranceSubSections;
  }

  private buildPaymentSubSection(
    coupon: Coupon,
    subSectionExpandedId: SubSectionIdSummaryLite
  ): SubSectionSummaryLite[] | [] {
    let paymentSubSections: SubSectionSummaryLite[] = [];

    const payment = coupon?.payment;
    const ownerKind = payment?.bankAccountOwner?.bankAccountOwnerKind;
    const accountOwner =
      ownerKind === OwnerKind.OTHER
        ? getPersonType(coupon?.payment?.bankAccountOwner)
        : ownerKind === OwnerKind.POLICY_HOLDER
          ? getPersonType(coupon?.policyholder)
          : undefined;

    const paymentSubSection: SubSectionSummaryLite = {
      id: SectionIdSummaryLite.PAYMENT,
      iban_number: payment?.iban?.iban ?? '-',
      account_owner:
        accountOwner && 'firstname' in accountOwner && accountOwner?.firstname && accountOwner?.surname
          ? `${accountOwner.firstname} ${accountOwner.surname}`
          : '-',
    };

    if (subSectionExpandedId === SubSectionIdSummaryLite.PAYMENT || hasContent(paymentSubSection)) {
      paymentSubSections.push(paymentSubSection);
    }

    return paymentSubSections;
  }

  private getSelectedCoverages(recommendations: Recommendation): CoverageSummaryLite[] {
    let coverages: CoverageSummaryLite[] = [];
    let coverage: CoverageSummaryLite;

    const promotionalCoverageInfo = recommendations?.recommendations?.kaskoClever;
    const additionalCoverages = recommendations.recommendations.tariffOptionsBasic;

    if (promotionalCoverageInfo?.isSelected) {
      const priceBefore = parseFloat(promotionalCoverageInfo.discount.priceBefore);
      const priceWithDiscount = parseFloat(promotionalCoverageInfo.discount.priceWithDiscount);

      coverage = {
        name: this.translateService.instant('SUMMARY_LITE.SECTION.COVERAGE.NORMAL_COVERAGE.KASKO_CLEVER', {
          discount: `${extractPercentage(promotionalCoverageInfo?.discount?.percentage)}%`,
        }),
        value: `-${(priceBefore - priceWithDiscount)?.toFixed(2)?.replace('.', ',')} €`,
      };

      coverages.push(coverage);
    }

    additionalCoverages.forEach(coverageAux => {
      if (coverageAux?.isSelected) {
        coverage = {
          name: this.translateService.instant(`SUMMARY_LITE.SECTION.COVERAGE.COVERAGE_NAME.${coverageAux?.id}`),
          value: `${coverageAux?.priceDefault?.replace('.', ',')} €`,
        };

        coverages.push(coverage);
      }
    });

    return coverages;
  }
}
